import { ExchangeCode } from "@/lib/market-data/market-data.types";
import { getLiveCandleService } from "./live-candle-service";
import {
  CandleErrorCallback,
  CandleInterval,
  CandleUpdate,
  CandleUpdateCallback,
} from "./candle.types";

const IDLE_DISCONNECT_MS = 45_000;

interface CandleSubscriber {
  onUpdate: CandleUpdateCallback;
  onError?: CandleErrorCallback;
}

interface CandleRegistryEntry {
  refCount: number;
  subscribers: Set<CandleSubscriber>;
  cleanupAdapter: () => void;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

const registry = new Map<string, CandleRegistryEntry>();

export function getLiveCandleStreamKey(
  exchange: ExchangeCode,
  symbol: string,
  interval: CandleInterval
): string {
  return `${exchange}:${symbol}:${interval}`;
}

export function subscribeSharedLiveCandles(
  exchange: ExchangeCode,
  symbol: string,
  interval: CandleInterval,
  subscriber: CandleSubscriber
): () => void {
  const key = getLiveCandleStreamKey(exchange, symbol, interval);
  let entry = registry.get(key);

  if (!entry) {
    const subscribers = new Set<CandleSubscriber>();
    const service = getLiveCandleService();

    const cleanupAdapter = service.subscribeCandles(
      exchange,
      symbol,
      interval,
      (update: CandleUpdate) => {
        const current = registry.get(key);
        if (!current) return;
        current.subscribers.forEach((listener) => listener.onUpdate(update));
      },
      (error) => {
        const current = registry.get(key);
        if (!current) return;
        current.subscribers.forEach((listener) => listener.onError?.(error));
      }
    );

    entry = {
      refCount: 0,
      subscribers,
      cleanupAdapter,
      idleTimer: null,
    };
    registry.set(key, entry);
  }

  if (entry.idleTimer) {
    clearTimeout(entry.idleTimer);
    entry.idleTimer = null;
  }

  entry.refCount += 1;
  entry.subscribers.add(subscriber);

  return () => {
    const current = registry.get(key);
    if (!current) return;

    current.subscribers.delete(subscriber);
    current.refCount -= 1;
    if (current.refCount > 0) return;

    current.idleTimer = setTimeout(() => {
      const active = registry.get(key);
      if (!active || active.refCount > 0) return;
      active.cleanupAdapter();
      registry.delete(key);
    }, IDLE_DISCONNECT_MS);
  };
}

